import {
  formatDatumTijd,
  getStatusDotClass,
  getWerkbonTitle,
  shouldPreviewDocument,
  toDocumentUrl,
} from "../../pages/monteur/workbonViewUtils";
import {
  getDemoClientName,
  getDemoLocationName,
  getDemoWorkorderNumber,
  getDemoWorkorderTitle,
  useMarketingDemoMode,
} from "../../utils/marketingDemo";

export function ActiveWerkbonBanner({ werkbon }) {
  const marketingDemoMode = useMarketingDemoMode();

  if (!werkbon) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
        Geen werkbon geselecteerd.
      </div>
    );
  }

  const statusClass = getStatusDotClass(werkbon?.status);
  const title = marketingDemoMode ? getDemoWorkorderTitle(0) : getWerkbonTitle(werkbon);
  const number = marketingDemoMode
    ? getDemoWorkorderNumber(0)
    : werkbon?.werkbonNummer || werkbon?.nummer || "Werkbon";
  const client = marketingDemoMode
    ? getDemoClientName()
    : werkbon?.clientName ||
      werkbon?.client?.naam ||
      werkbon?.clientId?.naam ||
      werkbon?.klant?.naam ||
      "-";
  const location = marketingDemoMode
    ? getDemoLocationName()
    : werkbon?.locationText || werkbon?.opdracht?.locatie || werkbon?.locatie || "-";
  const time = werkbon?.plannedStartTime || "-";

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-slate-900">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Actieve bon
          </p>
          <p className="truncate font-semibold text-slate-900">{number}</p>
        </div>
        <p className="inline-flex items-center gap-2 text-xs text-slate-600">
          <span className={`h-2 w-2 rounded-full ${statusClass}`} />
          {werkbon?.status || "-"}
        </p>
      </div>
      <p className="mt-1 truncate text-slate-700">{title}</p>
      <p className="mt-2 text-xs text-slate-600">
        {client} • {time} • {location}
      </p>
      <p className="mt-1 inline-flex items-center gap-2 text-xs text-slate-500">
        <span className={`h-2 w-2 rounded-full ${statusClass}`} />
        Vandaag actief
      </p>
    </div>
  );
}

export function WerkbonSelectionCard({
  item,
  isOpen,
  isMarkedToday,
  onClick,
  locked = false,
  lockLabel = "",
}) {
  const marketingDemoMode = useMarketingDemoMode();
  const statusClass = getStatusDotClass(item?.status);
  const showGreen = isMarkedToday || isOpen;
  const fallbackIndex =
    Math.max(Number(String(item?.werkbonNummer || "").replace(/\D+/g, "")) || 1, 1) - 1;
  const workorderNumber = marketingDemoMode
    ? getDemoWorkorderNumber(fallbackIndex)
    : item.werkbonNummer || item.nummer || "Werkbon";
  const workorderTitle = marketingDemoMode
    ? getDemoWorkorderTitle(fallbackIndex)
    : item.omschrijving || item.opdracht?.omschrijving || "-";
  const client = marketingDemoMode
    ? getDemoClientName()
    : item?.clientName || item?.clientId?.naam || item?.klant?.naam || "-";
  const location = marketingDemoMode
    ? getDemoLocationName()
    : item?.locationText || item?.opdracht?.locatie || item?.locatie || "-";
  const startTime = item?.plannedStartTime || "-";
  const demoLocationHint = marketingDemoMode ? ` • ${getDemoLocationName()}` : "";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={locked}
      className={`w-full rounded-lg border p-3 text-left text-sm ${
        showGreen
          ? "border-green-600 bg-green-600 text-white"
          : isOpen
          ? "border-blue-600 bg-blue-50"
          : "border-gray-200 bg-white"
      } ${locked ? "cursor-not-allowed opacity-75" : ""}`}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="font-semibold">{workorderNumber}</p>
        {showGreen && <span className="sr-only">Geselecteerd</span>}
      </div>

      <p className={`truncate ${showGreen ? "text-green-50" : "text-gray-600"}`}>
        {workorderTitle}
      </p>
      <p className={`mt-1 text-[11px] ${showGreen ? "text-green-100" : "text-gray-500"}`}>
        {client}
        {marketingDemoMode ? demoLocationHint : ` • ${location}`}
      </p>
      <p className={`mt-1 text-[11px] ${showGreen ? "text-green-100" : "text-gray-500"}`}>
        Start: {startTime}
      </p>
      <p
        className={`mt-1 inline-flex items-center gap-2 text-xs ${
          showGreen ? "text-green-100" : "text-gray-500"
        }`}
      >
        <span className={`h-2 w-2 rounded-full ${statusClass}`} />
        {item.status || "-"}
      </p>

      {locked ? (
        <p className={`mt-1 text-xs ${showGreen ? "text-green-100" : "text-amber-700"}`}>
          {lockLabel || "Werkbon is zichtbaar, maar nog niet open."}
        </p>
      ) : null}
    </button>
  );
}

export function StatusLegend({ compact = false, defaultOpen = false }) {
  return (
    <details
      open={defaultOpen}
      className={`monteur-status-legend mb-3 rounded border bg-gray-50 ${compact ? "p-2" : "p-3"}`}
    >
      <summary className="cursor-pointer text-sm font-semibold text-gray-700">Legenda</summary>
      <div className="monteur-status-legend-content mt-2 flex flex-wrap gap-3 text-xs text-gray-700">
        <LegendItem dotClass="bg-orange-500" label="In uitvoering" />
        <LegendItem dotClass="bg-red-600" label="Gestopt" />
        <LegendItem dotClass="bg-emerald-500" label="Afgerond" />
      </div>
    </details>
  );
}

function LegendItem({ dotClass, label }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`h-2.5 w-2.5 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
}

export function StatusFilterChips({ value, onChange }) {
  const options = [
    { key: "all", label: "Alle" },
    { key: "open", label: "Openstaand" },
    { key: "active", label: "Actief" },
    { key: "paused", label: "Gestopt" },
    { key: "done", label: "Afgerond" },
  ];

  return (
    <div className="mb-3 flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          onClick={() => onChange(option.key)}
          className={`rounded px-2.5 py-1.5 text-xs font-medium ${
            value === option.key
              ? "monteur-chip-active bg-blue-700 text-white"
              : "monteur-chip-inactive border border-gray-300 bg-white text-gray-700"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function DocumentRow({ doc }) {
  const href = toDocumentUrl(doc?.url || doc?.bestand || "");
  const previewable = shouldPreviewDocument(doc);
  const downloadName = doc?.name || doc?.title || "document";

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded border p-3">
      <div>
        <p className="text-sm font-medium text-gray-900">{doc.title || doc.name || "Document"}</p>
        <p className="text-xs text-gray-600">
          {doc.category || "document"} | {formatDatumTijd(doc.uploadedAt)}
        </p>
      </div>
      <div className="flex gap-2">
        <a
          href={href}
          target={previewable ? "_blank" : undefined}
          rel={previewable ? "noreferrer" : undefined}
          download={previewable ? undefined : downloadName}
          className="rounded border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          {previewable ? "Open" : "Download"}
        </a>
        {previewable ? (
          <a
            href={href}
            download={downloadName}
            className="rounded border px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            Download
          </a>
        ) : null}
      </div>
    </div>
  );
}

export function CompactInfoCard({ label, value }) {
  return (
    <div className="rounded border bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-2 text-sm text-gray-800">{value || "-"}</p>
    </div>
  );
}
